import type { ActivityResponse } from "@/lib/humhub-api/types";
import { parseFirstNumericParam } from "@/lib/utils/routeParams";

const CFILES_FOLDER_MODEL = "humhub\\modules\\cfiles\\models\\Folder";
const CFILES_FILE_MODEL = "humhub\\modules\\cfiles\\models\\File";
const COMMENT_NEW_ACTIVITY = "humhub\\modules\\comment\\activities\\NewComment";
const COMMENT_MODEL = "humhub\\modules\\comment\\models\\Comment";
const REACTION_ACTIVITY =
  "OSINTech\\humhub\\modules\\reaction\\activities\\Reactioned";
const POST_MODEL = "humhub\\modules\\post\\models\\Post";
const CONTENT_CREATED_ACTIVITY =
  "humhub\\modules\\content\\activities\\ContentCreated";
const CALENDAR_ENTRY_TYPE = "calendar_entry";

type ActivityDestination = {
  to: string;
  params: Record<string, string>;
  search?: Record<string, string>;
};

const getObjectRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : null;

const getSourceRecord = (source: ActivityResponse["source"]) =>
  getObjectRecord(source);

const getContentRecord = (sourceRecord: Record<string, unknown>) =>
  getObjectRecord(sourceRecord.content);

const getMetadataRecord = (contentRecord: Record<string, unknown> | null) =>
  contentRecord ? getObjectRecord(contentRecord.metadata) : null;

const getNestedContentRecord = (sourceRecord: Record<string, unknown>) => {
  const sourceContent = getContentRecord(sourceRecord);
  return sourceContent ? getContentRecord(sourceContent) : null;
};

const buildFolderDestination = (
  source: ActivityResponse["source"],
  studyGroupIdParam: string,
): ActivityDestination | null => {
  const sourceRecord = getSourceRecord(source);
  if (!sourceRecord) {
    return null;
  }

  const content = getContentRecord(sourceRecord);
  const metadata = getMetadataRecord(content);
  const isFolderByMeta = metadata?.object_model === CFILES_FOLDER_MODEL;
  // Some activity payloads omit object_model, so we fall back to shape checks here.
  const isFolderByProps =
    content?.title &&
    sourceRecord.parent_folder_id !== undefined &&
    !sourceRecord.file_name;

  if (!isFolderByMeta && !isFolderByProps) {
    return null;
  }

  const folderId = parseFirstNumericParam(
    metadata?.object_id,
    sourceRecord.object_id as unknown,
    (source as Record<string, unknown>).objectId,
    (source as Record<string, unknown>).id,
  );
  if (!folderId) return null;
  return {
    to: "/study-group/$study-group-id/files/folder/$folder-id",
    params: {
      "study-group-id": studyGroupIdParam,
      "folder-id": String(folderId),
    },
  };
};

const buildFileDestination = (
  source: ActivityResponse["source"],
  studyGroupIdParam: string,
): ActivityDestination | null => {
  const sourceRecord = getSourceRecord(source);
  if (!sourceRecord) {
    return null;
  }
  const content = getContentRecord(sourceRecord);
  const metadata = getMetadataRecord(content);

  if (metadata?.object_model !== CFILES_FILE_MODEL && !sourceRecord.file_name) {
    return null;
  }
  const parentFolderId =
    sourceRecord.parent_folder_id ||
    (sourceRecord.parent_folder as Record<string, unknown>)?.id;
  const fileId = sourceRecord.id;
  if (!parentFolderId) {
    return {
      to: "/study-group/$study-group-id/files",
      params: { "study-group-id": studyGroupIdParam },
      search: { file_id: String(fileId) },
    };
  }
  return {
    to: "/study-group/$study-group-id/files/folder/$folder-id",
    params: {
      "study-group-id": studyGroupIdParam,
      "folder-id": String(parentFolderId),
    },
    search: { file_id: String(fileId) },
  };
};

const buildCommentDestination = (
  activityClass: string,
  source: ActivityResponse["source"],
  studyGroupIdParam: string,
): ActivityDestination | null => {
  const sourceRecord = getSourceRecord(source);
  if (!sourceRecord) {
    return null;
  }
  if (
    activityClass !== COMMENT_NEW_ACTIVITY &&
    sourceRecord.objectModel !== COMMENT_MODEL
  ) {
    return null;
  }

  const sourceContent = getContentRecord(sourceRecord);
  const nestedContent = getNestedContentRecord(sourceRecord);
  const objectModel =
    sourceRecord.objectModel ??
    sourceRecord.object_model ??
    sourceContent?.objectModel ??
    sourceContent?.object_model;
  const isCommentObjectModel =
    typeof objectModel === "string" &&
    objectModel.toLowerCase().includes("comment");

  const postId = parseFirstNumericParam(
    sourceRecord.parentPostId,
    sourceContent?.parentPostId,
    nestedContent?.parentPostId,
    sourceContent?.objectId,
    sourceContent?.object_id,
    nestedContent?.objectId,
    nestedContent?.object_id,
    isCommentObjectModel ? null : sourceRecord.objectId,
    isCommentObjectModel ? null : sourceRecord.object_id,
  );
  if (!postId) return null;

  const commentId = parseFirstNumericParam(
    sourceRecord.id,
    sourceRecord.objectId,
  );
  const search = commentId ? { comment_id: String(commentId) } : undefined;

  return {
    to: "/study-group/$study-group-id/post/$post-id",
    params: {
      "study-group-id": studyGroupIdParam,
      "post-id": String(postId),
    },
    search,
  };
};

const buildReactionDestination = (
  activityClass: string,
  source: ActivityResponse["source"],
  studyGroupIdParam: string,
): ActivityDestination | null => {
  if (activityClass !== REACTION_ACTIVITY) {
    return null;
  }

  const sourceRecord = getSourceRecord(source);
  if (!sourceRecord) {
    return null;
  }
  const reactionContent = getContentRecord(sourceRecord);
  const objectModel =
    reactionContent?.objectModel ||
    sourceRecord.objectModel ||
    sourceRecord.object_model;
  const objectId =
    reactionContent?.objectId ||
    sourceRecord.objectId ||
    sourceRecord.object_id ||
    null;
  const contentId = reactionContent?.id || null;
  const contentType = reactionContent?.type;
  const metadata = getMetadataRecord(reactionContent);
  const sourceType = sourceRecord.type;

  const buildReactionCommentDestination = () => {
    const postId = reactionContent?.parentPostId;
    const commentId = contentId;
    if (!postId || !commentId) return null;
    return {
      to: "/study-group/$study-group-id/post/$post-id",
      params: {
        "study-group-id": studyGroupIdParam,
        "post-id": String(postId),
      },
      search: { comment_id: String(commentId) },
    };
  };

  const buildReactionPostDestination = () => {
    const postId =
      sourceType === "post" || contentType === "post"
        ? parseFirstNumericParam(
            reactionContent?.object_id,
            metadata?.object_id,
            sourceRecord.id,
            sourceRecord.object_id,
            sourceRecord.objectId,
            contentId,
            objectId,
          )
        : (contentId ?? objectId);
    if (!postId) return null;
    return {
      to: "/study-group/$study-group-id/post/$post-id",
      params: {
        "study-group-id": studyGroupIdParam,
        "post-id": String(postId),
      },
    };
  };

  if (contentType === "comment") {
    return buildReactionCommentDestination();
  }
  if (contentType === "post") {
    return buildReactionPostDestination();
  }

  if (typeof objectModel === "string") {
    if (objectModel.includes("comment")) {
      return buildReactionCommentDestination();
    }
    if (objectModel.includes("post")) {
      return buildReactionPostDestination();
    }
  }
  return null;
};

const buildPostDestination = (
  activityClass: string,
  source: ActivityResponse["source"],
  studyGroupIdParam: string,
): ActivityDestination | null => {
  const sourceRecord = getSourceRecord(source);
  if (!sourceRecord) {
    return null;
  }
  const content = getContentRecord(sourceRecord);
  const metadata = getMetadataRecord(content);
  if (
    activityClass !== CONTENT_CREATED_ACTIVITY ||
    metadata?.object_model !== POST_MODEL
  ) {
    return null;
  }
  return {
    to: "/study-group/$study-group-id/post/$post-id",
    params: {
      "study-group-id": studyGroupIdParam,
      "post-id": String(sourceRecord.id),
    },
  };
};

const buildCalendarEntryDestination = (
  source: ActivityResponse["source"],
): ActivityDestination | null => {
  const sourceRecord = getSourceRecord(source);
  if (!sourceRecord) {
    return null;
  }

  if (sourceRecord.type !== CALENDAR_ENTRY_TYPE) {
    return null;
  }

  const content = getContentRecord(sourceRecord);
  const metadata = getMetadataRecord(content);
  const eventId = parseFirstNumericParam(
    sourceRecord.object_id,
    sourceRecord.objectId,
    sourceRecord.id,
    metadata?.object_id,
  );

  if (!eventId) return null;
  return {
    to: "/event-search/$event-id",
    params: { "event-id": String(eventId) },
  };
};

const getCalendarEventIdFromActivity = (
  activity: ActivityResponse,
): number | null => {
  const source = activity.source;
  if (!source || typeof source !== "object") return null;
  const sourceRecord = source as Record<string, unknown>;
  const content = getContentRecord(sourceRecord);
  const sourceType = sourceRecord.type;
  if (sourceType !== CALENDAR_ENTRY_TYPE) return null;
  const metadata = getMetadataRecord(content);
  return parseFirstNumericParam(
    content?.object_id,
    sourceRecord.object_id,
    sourceRecord.objectId,
    sourceRecord.id,
    content?.id,
    metadata?.object_id,
  );
};

export const getActivityDestination = (
  activity: ActivityResponse,
  groupIdForRoute: string | number | undefined,
): ActivityDestination | null => {
  if (!activity.source || !groupIdForRoute) return null;

  const source = activity.source;
  const activityClass = activity.class;
  const studyGroupIdParam = String(groupIdForRoute);
  return (
    buildFolderDestination(source, studyGroupIdParam) ??
    buildFileDestination(source, studyGroupIdParam) ??
    buildCommentDestination(activityClass, source, studyGroupIdParam) ??
    buildReactionDestination(activityClass, source, studyGroupIdParam) ??
    buildCalendarEntryDestination(source) ??
    buildPostDestination(activityClass, source, studyGroupIdParam)
  );
};

type ActivityClickAction =
  | { kind: "route"; destination: ActivityDestination }
  | { kind: "modal"; eventId: number };

export const getActivityClickAction = (
  activity: ActivityResponse,
  groupIdForRoute: string | number | undefined,
): ActivityClickAction | null => {
  const calendarEventId = getCalendarEventIdFromActivity(activity);
  if (calendarEventId) {
    return { kind: "modal", eventId: calendarEventId };
  }
  const destination = getActivityDestination(activity, groupIdForRoute);
  if (!destination) return null;
  return { kind: "route", destination };
};

export type { ActivityDestination, ActivityClickAction };
